import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  id?: string;
  className?: string;
  size?: 'sm' | 'md';
  invalid?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
}

export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  id,
  className,
  size = 'md',
  invalid,
  disabled,
  ...aria
}: SelectProps) {
  // Radix reserves `""` and will not commit a selection when Root is controlled
  // with an empty string — the trigger can look filled while state stays blank.
  const selected = value === '' ? undefined : value;
  const items = options.filter((option) => option.value !== '');
  return (
    <SelectPrimitive.Root value={selected} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        id={id}
        aria-invalid={invalid || undefined}
        className={cn(
          'bg-surface-sunken ease-soft inline-flex w-full items-center justify-between gap-2 rounded-md border px-3 text-left transition-[border-color,box-shadow] duration-[160ms]',
          'data-[placeholder]:text-text-faint focus:border-primary focus:ring-primary-ring/40 focus:ring-4 focus:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-60',
          size === 'sm' ? 'h-8 text-xs' : 'h-10 text-sm',
          invalid ? 'border-danger/70' : 'border-line-strong',
          className,
        )}
        {...aria}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <ChevronDown className="text-text-faint size-4 shrink-0" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className={cn(
            'border-line bg-surface-overlay z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border shadow-lg',
            'data-[state=open]:animate-pop',
          )}
        >
          <SelectPrimitive.Viewport className="p-1">
            {items.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'relative flex cursor-pointer items-center gap-2 rounded-sm py-2 pr-2 pl-8 text-sm outline-none select-none',
                  'data-[highlighted]:bg-primary-soft data-[highlighted]:text-primary',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                <SelectPrimitive.ItemIndicator className="absolute left-2.5">
                  <Check className="size-3.5" />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
